/**
 * The worker↔repo link record. Stored on the Foundry agent under
 * `metadata.brain` as a JSON STRING (Foundry metadata is a flat
 * Record<string,string>).
 *
 * This shipped originally in plugins/m8t/server/src/brain-link.ts, then moved
 * here so the CLI + gateway + plugin all import from one place. The
 * plugin server file becomes a thin re-export for back-compat.
 *
 * Two link modes:
 *   - PAT mode: installationId absent. Connection holds a static PAT.
 *   - App mode: installationId present. Connection holds an App-minted
 *     installation token, rotated lazily on every invoke.
 */
export type BrainTransport = "gateway-mcp-v1" | "github-mcp";

/** Top-level Foundry metadata key containing sha256(raw gateway MCP bearer). */
export const BRAIN_MCP_BEARER_HASH_METADATA_KEY = "brainMcpBearerHash";

/** Transport discriminator for the gateway-hosted, least-privilege Brain MCP. */
export const GATEWAY_BRAIN_MCP_TRANSPORT = "gateway-mcp-v1" as const;

export interface BrainLink {
  repo: string; // "owner/name"
  branch: string; // default "main"
  topology: "per-worker" | "shared"; // default "per-worker"
  schemaVersion: string; // brain skeleton version
  /**
   * Foundry project-connection name holding the GitHub credential.
   * Special value: `"in-container"` is the hosted-worker sentinel — the token
   * is minted INSIDE the container (no Foundry connection exists). Invoke-path
   * rotation MUST skip links with this value.
   */
  credentialRef: string;
  installationId?: string; // GitHub App installation id (App mode)
  instanceFolder?: string; // topology === "shared"
  /**
   * Prompt-agent Brain transport. Omitted links are legacy GitHub MCP links;
   * new gateway links stamp `gateway-mcp-v1`, while an explicit `github-mcp`
   * is retained as the operator rollback mode.
   */
  transport?: BrainTransport;
}

/**
 * Parse the brain link from Foundry agent metadata. Defensive: absent or
 * malformed returns undefined and never throws — discovery must not break on
 * a hand-written or partial `metadata.brain`.
 */
export function parseBrainLink(
  metadata: Record<string, string> | undefined,
): BrainLink | undefined {
  const raw = metadata?.brain;
  if (!raw) return undefined;
  try {
    const obj: Partial<BrainLink> | null = JSON.parse(raw) as Partial<BrainLink> | null;
    if (!obj || typeof obj.repo !== "string" || obj.repo.length === 0) return undefined;
    return {
      repo: obj.repo,
      branch: typeof obj.branch === "string" && obj.branch ? obj.branch : "main",
      topology: obj.topology === "shared" ? "shared" : "per-worker",
      schemaVersion:
        typeof obj.schemaVersion === "string" && obj.schemaVersion ? obj.schemaVersion : "1",
      credentialRef: typeof obj.credentialRef === "string" ? obj.credentialRef : "",
      ...(typeof obj.installationId === "string" ? { installationId: obj.installationId } : {}),
      ...(typeof obj.instanceFolder === "string" ? { instanceFolder: obj.instanceFolder } : {}),
      ...(obj.transport === "gateway-mcp-v1" || obj.transport === "github-mcp"
        ? { transport: obj.transport }
        : {}),
    };
  } catch {
    return undefined;
  }
}

/** True when the link uses GitHub App authentication. A type guard so
 *  callers that mint with the installation id narrow it to a present string. */
export function isAppMode(link: BrainLink): link is BrainLink & { installationId: string } {
  return typeof link.installationId === "string" && link.installationId.length > 0;
}

/** True only for an App-linked Brain explicitly bound to the gateway MCP. */
export function isGatewayMcpBrain(
  link: BrainLink,
): link is BrainLink & {
  installationId: string;
  transport: typeof GATEWAY_BRAIN_MCP_TRANSPORT;
} {
  return isAppMode(link) && link.transport === GATEWAY_BRAIN_MCP_TRANSPORT;
}

/** Sentinel credentialRef for hosted workers: the token is minted INSIDE the
 *  container (no Foundry connection). Invoke-path rotation must skip these. */
export const IN_CONTAINER_CREDENTIAL_REF = "in-container";

/** True when the link is a hosted/in-container brain (token minted in-sandbox;
 *  there is NO Foundry connection to rotate). */
export function isInContainer(link: BrainLink): boolean {
  return link.credentialRef === IN_CONTAINER_CREDENTIAL_REF;
}

/** Serialize a BrainLink to the Foundry-metadata JSON string. */
export function serializeBrainLink(link: BrainLink): string {
  return JSON.stringify(link);
}
